import { convertArrayByUnitType } from "../../simulation/results/utils/convertByUnitType";

/*
  Turns a chart from an Ask Cadenza answer into something LineChart can draw, in
  the user's own units. Maestro sends the engine's unit strings (the MCP tools'
  unitForColumn); this names the unit type for each so the conversion is the
  one the Results chart uses.
*/

const UNIT_TYPE_OF = {
  g: { unitType: "metal_mass", base: "g" },
  kg: { unitType: "mass", base: "kg" },
  t: { unitType: "mass", base: "t" },
  "m³": { unitType: "volume", base: "m³" },
  "g/m³": { unitType: "concentration", base: "g/m3" },
  "%": { unitType: "percentage_ratio", base: "%" },
  fraction: { unitType: "percentage_ratio", base: "fraction" },
};

const COLORS = ["#d993f5", "#4caf50", "#ff9800", "#9B9BD2", "#ECECF6", "#6464B9", "#40a9c4", "#e57373"];

/** One series in display units: { label, column, displayUnit, values }. */
export const convertSeries = (series, unitPreferences) => {
  const known = UNIT_TYPE_OF[series.unit];
  if (!known) {
    return { ...series, displayUnit: series.unit || "", values: series.values };
  }
  const { converted, displayUnit } = convertArrayByUnitType(
    series.values,
    known.base,
    known.unitType,
    unitPreferences
  );
  return { ...series, displayUnit, values: converted };
};

/** Chart.js data and options for one attachment. */
export const buildChartView = (chart, unitPreferences) => {
  const series = chart.series.map((s) => convertSeries(s, unitPreferences));
  const units = [...new Set(series.map((s) => s.displayUnit))];
  const datasets = series.map((s, i) => ({
    label: s.displayUnit ? `${s.label} (${s.displayUnit})` : s.label,
    data: s.values,
    borderColor: COLORS[i % COLORS.length],
    backgroundColor: "transparent",
    borderWidth: 2,
    pointRadius: chart.dates.length > 40 ? 0 : 2,
    spanGaps: true,
    yAxisID: units.indexOf(s.displayUnit) === 1 ? "y1" : "y",
  }));
  const axisTitle = (text) => ({ display: !!text, text, color: "#e2e8f0", font: { size: 11 } });
  const ticks = { color: "#e2e8f0", font: { size: 10 } };
  // The drawer is narrow, and these can be in the hundreds of millions.
  const compact = new Intl.NumberFormat(undefined, { notation: "compact", maximumSignificantDigits: 4 });
  const yTicks = { ...ticks, maxTicksLimit: 6, callback: (value) => compact.format(value) };
  const options = {
    plugins: {
      legend: {
        position: "bottom",
        labels: { color: "#e2e8f0", font: { size: 10 }, boxWidth: 8, padding: 8, usePointStyle: true },
      },
    },
    scales: {
      x: { ticks: { ...ticks, maxTicksLimit: 6, maxRotation: 0 }, grid: { color: "#374151" } },
      y: { title: axisTitle(units[0]), ticks: yTicks, grid: { color: "#374151" } },
      ...(units.length > 1
        ? {
            y1: {
              position: "right",
              title: axisTitle(units[1]),
              ticks: yTicks,
              grid: { drawOnChartArea: false },
            },
          }
        : {}),
    },
  };
  return { series, data: { labels: chart.dates, datasets }, options };
};

const csvCell = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const csvText = (rows) => rows.map((r) => r.map(csvCell).join(",")).join("\r\n");

/** The chart's data as CSV text, in the units it is drawn in. */
export const chartCsv = (chart, series) => {
  const header = ["Period", ...series.map((s) => (s.displayUnit ? `${s.label} (${s.displayUnit})` : s.label))];
  const rows = chart.dates.map((date, i) => [date, ...series.map((s) => s.values[i])]);
  return csvText([header, ...rows]);
};

export const downloadCsv = (text, fileName) => {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
